import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useListContext, useTranslate } from 'react-admin';
import { calcDaysFromSeriesData } from '@/components/analytics/PeriodFilters.jsx';
import { formatNumberWithComma } from '@/helpers/format/formatNumber.js';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { Palette } from '@/themes/themeOverrides/Palette';

const StyledBaseStat = styled(Box)(() => ({
    minWidth: '72px',
    width: '100%',
    '& .base-stat-content': {
        padding: '19px 40px',
        borderRadius: '16px',
        backgroundColor: Palette.white,
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            padding: '16px 24px',
        },
        '& h3': {
            marginTop: '1px',
        },
    },
    '& .base-stat-label': {
        fontSize: '16px',
        minWidth: '72px',
    },
    '& .base-stat-value': {
        minWidth: '72px',
    },
}));

const BaseStat = ({ label, value }) => (
    <StyledBaseStat>
        <Box className='base-stat-content'>
            <Typography className='base-stat-label'>{label}</Typography>
            <Typography className='base-stat-value' variant='h3' component='h3'>
                {formatNumberWithComma(value)}
            </Typography>
        </Box>
    </StyledBaseStat>
);

const StyledElement = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    padding: '9px 0',
    borderTop: `2px solid ${Palette.gray.gray75}`,
    [`@media (max-width: ${breakpoints.values.xl}px)`]: {
        padding: '10px 0',
    },
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        padding: '7px 0',
    },
    '& .element-label': {
        width: '50%',
        marginRight: '24px',
        fontSize: '14px',
        letterSpacing: '-0.14px',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            fontSize: '11px',
        },
    },
    '& .element-value': {
        fontWeight: 400,
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            fontSize: '18px',
        },
    },
    '& + .MuiBox-root': {
        borderTop: `2px solid ${Palette.gray.gray75}`,
    },
}));

const Element = ({ label, value }) => (
    <StyledElement className='element'>
        <Typography className='element-label'>{label}</Typography>
        <Typography variant='h5' component='h5' className='element-value'>
            {formatNumberWithComma(value)}
        </Typography>
    </StyledElement>
);

const StyledExtendedStat = styled(Box)(() => ({
    padding: '31px 40px 31px',
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        padding: '24px 24px 16px',
    },
    borderRadius: '16px',
    backgroundColor: Palette.white,
    '& .extended-stat-title': {
        marginBottom: '20px',
        letterSpacing: '-0.36px',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            marginBottom: '12px',
        },
    },
    '& .extended-stat-content': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
}));

const ExtendedStat = ({ days, transactionCount, collectionPointCount, avgPackages }) => {
    const translate = useTranslate();
    const getDaysForm = days => {
        if (days === 1) return translate('analytics.common.day');
        return translate('analytics.common.days');
    };

    return (
        <StyledExtendedStat>
            <Typography
                className='extended-stat-title'
                variant='h6'
                component='h6'
            >{`${translate('campaign.steps.summary')}: ${days} ${getDaysForm(days)}`}</Typography>
            <Box className='extended-stat-content'>
                <Element label={`${translate('common.transactionsNumber')} / ${days} ${getDaysForm(days)}`} value={transactionCount} />
                <Element label={translate('analytics.transaction.avgTransactionsPerCollectionPoint')} value={transactionCount / collectionPointCount} />
                <Element label={translate('analytics.transaction.avgPackagesPerCollectionPoint')} value={avgPackages / collectionPointCount} />
            </Box>
        </StyledExtendedStat>
    );
};

const StyledTransactionSummaryContainer = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: 'unset !important',
    [`@media (max-width: ${breakpoints.values.lg}px)`]: {
        flexDirection: 'column',
    },
    '& .base-stats-column': {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        marginRight: '32px',
        [`@media (max-width: ${breakpoints.values.lg}px)`]: {
            marginRight: '0',
            flexDirection: 'row',
            marginBottom: '32px',
        },
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            marginBottom: '18px',
            width: '100%',
        },
    },
    '& .extended-stats-section': {
        flex: 1,
        display: 'flex',
        gap: '32px',
        [`@media (max-width: ${breakpoints.values.lg}px)`]: {
            width: '100%',
        },
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            flexDirection: 'column',
            gap: '20px',
            flex: 0,
            width: '100%',
        },
        '& .MuiBox-root': {
            flex: 1,
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                flex: 0,
            },
        },
    },
}));

export const TransactionSummaryAnalytics = ({ id = 'current' }) => {
    const { data } = useListContext();
    const translate = useTranslate();

    if (!data || data.length === 0) return null;

    const analytic = data.find(s => s.id === id).data;
    const series = data.find(s => s.id === 'series').data;
    const days = calcDaysFromSeriesData(series);

    return (
        <StyledTransactionSummaryContainer>
            <Box className='base-stats-column'>
                <BaseStat label={translate('resources.deviceGroup.fields.deviceCount')} value={analytic.deviceCount} />
                <BaseStat label={translate('analytics.device.collectionPoint')} value={analytic.collectionPointCount} />
            </Box>
            <Box className='extended-stats-section'>
                <ExtendedStat
                    days={days}
                    transactionCount={analytic.transactionsCount}
                    collectionPointCount={analytic.collectionPointCount}
                    avgPackages={analytic.transactionsValidContainersCount}
                />
                <ExtendedStat
                    days={1}
                    transactionCount={analytic.transactionsCount / days}
                    collectionPointCount={analytic.collectionPointCount}
                    avgPackages={analytic.transactionsValidContainersCount / days}
                />
            </Box>
        </StyledTransactionSummaryContainer>
    );
};
